import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, FileText, AlertTriangle, RefreshCw, Download } from 'lucide-react';
import TicketPdfViewer from '../components/tickets/TicketPdfViewer';
import { API_URL } from '../api/client';
import { publicApiFetch, getPublicFetchMessage } from '../api/public';
import {
  STAMPED_UPLOAD_FILE_FIELD,
  STAMPED_UPLOAD_NAME_FIELD,
} from '../api/public';
import type { ApproveTicketBody, ReviewStamp, ReviewStampLive, StampedFileRef } from '../api/public';
import ApprovalStampForm from '../components/tickets/ApprovalStampForm';
import ApprovalFileUpload from '../components/tickets/ApprovalFileUpload';
import StampedFileNotice from '../components/tickets/StampedFileNotice';
import {
  missingStampInputs,
  parseReviewStamp,
  parseStampedFileRef,
  signerNameLabel,
} from '../lib/approval-stamp';

/**
 * THIS PAGE IS UNAUTHENTICATED, AND MUST STAY THAT WAY.
 *
 * The reader is a customer who opened a magic link from an email. They have no
 * FieldIQ account. The `token` in the URL is the whole of their authorisation,
 * and `/ticket-approval/*` is the only route surface that accepts it.
 *
 * Requests therefore go through `publicApiFetch`, NOT `apiClient`. See the
 * header of `src/api/public.ts` for what `apiClient` would do to this page —
 * briefly: attach the tenant session JWT if one happens to be in storage, and
 * turn a 401 (an expired review link — the ordinary case) into a wipe of that
 * token plus a redirect to /login. `publicApiFetch` sends no Authorization
 * header, cannot be made to, and touches no interceptor.
 *
 * What it does add is a 30s bound. Before PR #317 these three calls were plain
 * `fetch`es with no timeout: a stall left "Approving..." on screen forever,
 * because `setSubmitting(false)` sits in a `finally` that a never-settling
 * promise never reaches.
 */

interface TicketData {
  ticketNumber: string;
  workDate: string;
  startTime: string;
  endTime: string;
  totalHours: string;
  customerName: string;
  rigName: string;
  jobType: string;
  casingSizeInches: string;
  threadType: string;
  actualMetresRun: string;
  customerNotes: string;
  subtotal: string;
  totalAmount: string;
  currency: string;
  taxRate1Label: string;
  taxRate1Amount: string;
  taxRate2Label: string;
  taxRate2Amount: string;
  companyName: string;
  logoUrl: string;
  lineItems: { description: string; type: string; unit: string; rate: string; quantity: string; total: string }[];
  customerContactName: string;
  customerContactPhone: string;
  crew: { name: string; role: string }[];
  /** The customer's approval stamp: live while awaiting approval, frozen once signed, else null. */
  stamp: ReviewStamp | null;
  /**
   * The uploaded signed ticket, once one exists. Mutually exclusive with a
   * FROZEN stamp — the database forbids both (`tickets_customer_stamp_xor_file_chk`)
   * — but not with a LIVE one: a ticket still awaiting approval can carry the
   * operator's stamp template AND be approvable by upload instead, which is the
   * whole choice this page has to present.
   */
  stampedFile: StampedFileRef | null;
}

function mapTicketData(raw: any): TicketData {
  return {
    ticketNumber: raw.ticketNumber ?? raw.ticket_number ?? '',
    workDate: raw.workDate ?? raw.work_date ?? '',
    startTime: raw.startTime ?? raw.start_time ?? '',
    endTime: raw.endTime ?? raw.end_time ?? '',
    totalHours: raw.totalHours ?? raw.total_hours ?? '',
    customerName: raw.customerName ?? raw.customer_name ?? '',
    rigName: raw.rigName ?? raw.rig_name ?? '',
    jobType: raw.jobType ?? raw.job_type ?? '',
    casingSizeInches: raw.casingSizeInches ?? raw.casing_size_inches ?? '',
    threadType: raw.threadType ?? raw.thread_type ?? '',
    actualMetresRun: raw.actualMetresRun ?? raw.actual_metres_run ?? '',
    customerNotes: raw.customerNotes ?? raw.customer_notes ?? '',
    subtotal: raw.subtotal ?? '',
    totalAmount: raw.totalAmount ?? raw.total_amount ?? '',
    currency: raw.currency ?? '',
    taxRate1Label: raw.taxRate1Label ?? raw.tax_rate1_label ?? '',
    taxRate1Amount: raw.taxRate1Amount ?? raw.tax_rate1_amount ?? '',
    taxRate2Label: raw.taxRate2Label ?? raw.tax_rate2_label ?? '',
    taxRate2Amount: raw.taxRate2Amount ?? raw.tax_rate2_amount ?? '',
    companyName: raw.companyName ?? raw.company_name ?? '',
    logoUrl: raw.logoUrl ?? raw.logo_url ?? '',
    lineItems: (raw.lineItems ?? raw.line_items ?? []).map((li: any) => ({
      description: li.description ?? '',
      type: li.type ?? '',
      unit: li.unit ?? '',
      rate: li.rate ?? '',
      quantity: li.quantity ?? li.qty ?? '',
      total: li.total ?? li.amount ?? '',
    })),
    customerContactName: raw.customerContactName ?? raw.customer_contact_name ?? '',
    customerContactPhone: raw.customerContactPhone ?? raw.customer_contact_phone ?? '',
    crew: (raw.crew ?? []).map((c: any) => ({
      name: c.name ?? '',
      role: c.role ?? '',
    })),
    stamp: parseReviewStamp(raw.stamp),
    // Reads `raw`, not `raw.stamped_file`: the two spellings the API sends carry
    // DIFFERENT inner keys (`uploaded_at` vs `uploadedAt`), so the parser has to
    // see both outer keys to pair each with its own.
    stampedFile: parseStampedFileRef(raw),
  };
}

/**
 * The ticket number, kept on the page as HTML, on its own line.
 *
 * It used to reach the reader only because the old HTML render drew the whole
 * ticket. The PDF shows it too, but a reader whose PDF has not loaded — or who
 * has fallen through to the download message — would otherwise have nothing on
 * screen naming which ticket they are about to approve.
 *
 * Its own line rather than appended to the label: at phone width the header
 * already wraps the company name, and a separator between the two left a
 * dangling "\u00b7" at the end of a line. Absent renders as nothing at all —
 * never a bare separator, never an empty element.
 */
const ReviewSubtitle: React.FC<{ ticketNumber: string }> = ({ ticketNumber }) => (
  <>
    <p className="text-sm text-gray-500">Field Ticket Review</p>
    {ticketNumber && (
      <p className="text-sm font-semibold text-gray-700" data-testid="review-ticket-number">
        {ticketNumber}
      </p>
    )}
  </>
);

type ErrorType = 'not_found' | 'conflict' | 'server' | 'network';

interface ReviewState {
  loading: boolean;
  ticket: TicketData | null;
  errorType: ErrorType | null;
  errorMessage: string;
  readOnly: boolean;
  readOnlyReason: string;
  approvedAt: string | null;
  decision: string | null;
  decisionByName: string | null;
  decisionByTitle: string | null;
}

const TicketReviewPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<ReviewState>({
    loading: true,
    ticket: null,
    errorType: null,
    errorMessage: '',
    readOnly: false,
    readOnlyReason: '',
    approvedAt: null,
    decision: null,
    decisionByName: null,
    decisionByTitle: null,
  });
  const [actioned, setActioned] = useState(false);
  const [actionType, setActionType] = useState<'approved' | 'rejected' | ''>('');
  const [submitting, setSubmitting] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const rejectReasonRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (showReject) rejectReasonRef.current?.focus();
  }, [showReject]);
  const [rejectReason, setRejectReason] = useState('');
  const [approverName, setApproverName] = useState('');
  const [approverTitle, setApproverTitle] = useState('');
  const [stampValues, setStampValues] = useState<Record<string, string>>({});
  const [signature, setSignature] = useState<string | null>(null);
  /**
   * WHICH of the two approval paths the customer is on.
   *
   * One piece of state, not two flags, because the database treats these as
   * exclusive: `tickets_customer_stamp_xor_file_chk` permits `customer_stamp` OR
   * `customer_stamp_file` and never both. A page that let someone fill the stamp
   * and attach a file would be collecting something the server cannot store —
   * the upload route answers 409 rather than letting the constraint fire, but a
   * clean refusal after five minutes of typing is still the page's fault. So
   * only one panel is ever mounted, and choosing is the first thing you do.
   */
  const [path, setPath] = useState<'form' | 'upload'>('form');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  /** The upload route's refusal, verbatim. Not merged into the page-level error. */
  const [uploadError, setUploadError] = useState<string | null>(null);

  const loadTicket = useCallback(async () => {
    if (!token) return;
    setState(s => ({ ...s, loading: true, errorType: null, errorMessage: '' }));

    try {
      const res = await publicApiFetch(`/ticket-approval/review/${token}`);

      if (res.status === 404) {
        setState(s => ({
          ...s,
          loading: false,
          errorType: 'not_found',
          errorMessage: 'This review link is invalid or has expired. Please contact the company that sent it.',
        }));
        return;
      }

      if (res.status === 409) {
        let body: any = {};
        try { body = await res.json(); } catch {}
        setState(s => ({
          ...s,
          loading: false,
          errorType: 'conflict',
          errorMessage: body.error || 'This ticket is not available for review.',
        }));
        return;
      }

      if (!res.ok) {
        setState(s => ({
          ...s,
          loading: false,
          errorType: 'server',
          errorMessage: "We're having trouble loading this ticket. Please try again in a moment.",
        }));
        return;
      }

      const json = await res.json();

      if (json.readOnly) {
        const raw = json.data || json.ticket || null;
        setState(s => ({
          ...s,
          loading: false,
          ticket: raw ? mapTicketData(raw) : null,
          readOnly: true,
          readOnlyReason: json.reason || 'already_actioned',
          approvedAt: json.approvedAt || json.decidedAt || json.data?.approvedAt || null,
          decision: json.decision || json.reason || null,
          // `decidedBy` is the key the API actually sends (ticket-approval.routes.ts);
          // the others are older spellings kept for tolerance.
          decisionByName: json.decisionByName || json.decidedBy || json.approverName || json.data?.approverName || null,
          decisionByTitle: json.decisionByTitle || json.approverTitle || json.data?.approverTitle || null,
        }));
        return;
      }

      if (json.success === false) {
        if (json.alreadyActioned) {
          setActioned(true);
        }
        setState(s => ({
          ...s,
          loading: false,
          errorType: 'server',
          errorMessage: json.error || 'Failed to load ticket',
        }));
        return;
      }

      setState(s => ({
        ...s,
        loading: false,
        ticket: mapTicketData(json.data),
      }));
    } catch (err) {
      setState(s => ({
        ...s,
        loading: false,
        errorType: 'network',
        // A timeout and a dead network read differently to someone who cannot
        // tell a broken link from a slow one; both land on the same screen,
        // which carries a Try Again button.
        errorMessage: getPublicFetchMessage(
          err,
          'Unable to connect. Please check your internet connection and try again.'
        ),
      }));
    }
  }, [token]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  const liveStamp: ReviewStampLive | null = state.ticket?.stamp && !state.ticket.stamp.frozen ? state.ticket.stamp : null;

  // `stampValues` holds only what the approver has typed. An untouched row
  // reads the server's prefill, so the first paint already shows it and no
  // refetch can overwrite typing. Read-only rows are never in here.
  const effectiveStampValues: Record<string, string> = {};
  if (liveStamp) {
    for (const row of liveStamp.rows) {
      if (!row.read_only) effectiveStampValues[row.field_id] = stampValues[row.field_id] ?? row.value;
    }
  }

  const missingForApproval = liveStamp ? missingStampInputs(liveStamp, effectiveStampValues, approverName, signature) : [];

  /** No stamp: exactly the body this page has always sent. */
  const approveBody = (): ApproveTicketBody => {
    if (!liveStamp) return { token: token!, approverName, approverTitle };
    return {
      token: token!,
      approverName: approverName.trim(),
      stampValues: effectiveStampValues,
      signature: signature ?? undefined,
      stampTemplateVersion: liveStamp.template_version,
    };
  };

  const handleApprove = async () => {
    // Two layers, on purpose. The disabled button is UI state; this is the
    // guard. Anything that reaches the handler with an incomplete or unsigned
    // stamp — a stale render, a programmatic click — sends nothing.
    if (!token || missingForApproval.length > 0) return;
    setSubmitting(true);
    try {
      const res = await publicApiFetch('/ticket-approval/approve', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(approveBody()),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setState(s => ({ ...s, errorType: 'server', errorMessage: body.error || 'Approval failed. Please try again.' }));
        return;
      }
      const data = await res.json();
      if (data.success) {
        setActionType('approved');
        setActioned(true);
        // The approve response carries no stamp. Re-read the ticket so the
        // confirmation shows the copy the server actually froze, not what this
        // page believes it sent.
        if (liveStamp) loadTicket();
      } else {
        setState(s => ({ ...s, errorType: 'server', errorMessage: data.error || 'Approval failed' }));
      }
    } catch (err) {
      setState(s => ({
        ...s,
        errorType: 'network',
        errorMessage: getPublicFetchMessage(err, 'Unable to connect. Please try again.'),
      }));
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Approve by uploading the signed ticket.
   *
   * MULTIPART, AND NO Content-Type HEADER. `publicApiFetch` passes `init.headers`
   * through as given, so naming `multipart/form-data` here would send it WITHOUT
   * the boundary the browser computes, and the server would fail to parse a body
   * it was told to expect — the same rule as every `apiClient` upload call site,
   * where an interceptor strips it precisely because it kept being set.
   *
   * The two field names come from `api/public.ts`. They are the one part of this
   * TypeScript cannot check, and `approverName` is camelCase while most write
   * bodies in this API are snake_case.
   *
   * Nothing about the file is inspected before sending. Size, type and rate
   * limits are the server's rules, checked against the bytes rather than the
   * name, and every refusal is rendered as written.
   */
  const handleUpload = async () => {
    if (!token || !uploadFile || approverName.trim() === '') return;
    setUploadError(null);
    setSubmitting(true);
    try {
      const form = new FormData();
      form.append(STAMPED_UPLOAD_FILE_FIELD, uploadFile);
      form.append(STAMPED_UPLOAD_NAME_FIELD, approverName.trim());

      const res = await publicApiFetch(`/ticket-approval/${token}/upload`, {
        method: 'POST',
        body: form,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({} as any));
        // Every refusal this route can give — 429 rate limit, 415 wrong type,
        // 413 oversize, 409 already approved, 410 expired — arrives as
        // `{ success: false, error: '<sentence>' }` and is shown unchanged. The
        // fallback covers only a body that was not JSON at all.
        setUploadError(body.error || 'The upload could not be completed. Please try again.');
        return;
      }

      const data = await res.json().catch(() => ({} as any));
      if (data.success === false) {
        setUploadError(data.error || 'The upload could not be completed. Please try again.');
        return;
      }

      setActionType('approved');
      setActioned(true);
      // The 200 carries only a message — no filename, no timestamp. Re-read the
      // ticket so the confirmation shows the record the SERVER stored rather
      // than the File object this page happens to be holding.
      loadTicket();
    } catch (err) {
      setUploadError(
        getPublicFetchMessage(err, 'Unable to connect. Please check your internet connection and try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    if (!token || !rejectReason.trim()) return;
    setSubmitting(true);
    try {
      const res = await publicApiFetch('/ticket-approval/reject', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, reason: rejectReason }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        setState(s => ({ ...s, errorType: 'server', errorMessage: body.error || 'Rejection failed. Please try again.' }));
        return;
      }
      const data = await res.json();
      if (data.success) {
        setActionType('rejected');
        setActioned(true);
        setShowReject(false);
      } else {
        setState(s => ({ ...s, errorType: 'server', errorMessage: data.error || 'Rejection failed' }));
      }
    } catch (err) {
      setState(s => ({
        ...s,
        errorType: 'network',
        errorMessage: getPublicFetchMessage(err, 'Unable to connect. Please try again.'),
      }));
    } finally {
      setSubmitting(false);
    }
  };

  const { loading, ticket, errorType, errorMessage, readOnly, approvedAt, decision, decisionByName, decisionByTitle } = state;

  // Declared above every early return: all three views below render the PDF.
  const pdfUrl = token ? `${API_URL}/ticket-approval/review/${token}/pdf` : null;

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-amber-600 animate-spin" />
      </div>
    );
  }

  if (actioned) {
    return (
      <div className="min-h-full bg-gray-50">
        {ticket && (
          <div className="bg-white border-b px-6 py-4">
            <div className="max-w-5xl mx-auto flex items-center gap-3">
              {ticket.logoUrl && <img src={ticket.logoUrl} alt="" className="h-8" />}
              <div>
                <h1 className="font-bold text-gray-900">{ticket.companyName}</h1>
                <ReviewSubtitle ticketNumber={ticket.ticketNumber} />
              </div>
            </div>
          </div>
        )}
        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
            {actionType === 'approved' ? (
              <>
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Ticket Approved</h1>
                <p className="text-gray-600">Thank you! The service company has been notified.</p>
              </>
            ) : actionType === 'rejected' ? (
              <>
                <AlertTriangle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Revision Requested</h1>
                <p className="text-gray-600">The service company has been notified of your feedback.</p>
              </>
            ) : (
              <>
                <FileText className="w-16 h-16 text-blue-500 mx-auto mb-4" />
                <h1 className="text-2xl font-bold text-gray-900 mb-2">Already Actioned</h1>
                <p className="text-gray-600">This ticket has already been reviewed.</p>
              </>
            )}
          </div>
          {/* Exactly one of these can exist — the xor constraint guarantees it —
              so they are separate conditions rather than a ternary pretending
              to choose between two things that could both be true. */}
          {ticket?.stampedFile && (
            <div className="mt-4">
              <StampedFileNotice stampedFile={ticket.stampedFile} />
            </div>
          )}
          {/* The PDF carries the frozen stamp itself — signature, approver
              names, signed date — so this is the signed document, not a
              rendering of it beside one. */}
          {ticket?.stamp?.frozen && pdfUrl && (
            <div className="mt-4">
              <TicketPdfViewer pdfUrl={pdfUrl} testId="ticket-pdf-viewer-actioned" />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (readOnly && ticket) {
    const formattedDate = approvedAt
      ? new Date(approvedAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
      : null;
    const isRejected = decision === 'rejected' || decision === 'revision_requested';
    // The office billed this ticket without a customer decision. The API sends
    // `decision: 'invoiced'` for exactly that case; it used to fall into the
    // "Approved." branch with no name and no date, telling a customer who never
    // signed that they had. There is no late-approval path — say what happened.
    const isInvoiced = decision === 'invoiced';
    const bannerBg = isRejected ? 'bg-red-50 border-red-200' : isInvoiced ? 'bg-gray-50 border-gray-200' : 'bg-green-50 border-green-200';
    const BannerIcon = isRejected ? XCircle : isInvoiced ? FileText : CheckCircle;
    const bannerIconColor = isRejected ? 'text-red-500' : isInvoiced ? 'text-gray-500' : 'text-green-500';
    const bannerTitle = isRejected ? 'Revision Requested' : isInvoiced ? 'Already Invoiced' : 'Approved';

    const approverParts: string[] = [];
    if (decisionByName) approverParts.push(decisionByName);
    if (decisionByTitle) approverParts.push(decisionByTitle);
    const approverStr = approverParts.length > 0 ? approverParts.join(', ') : null;

    return (
      <div className="min-h-full bg-gray-50">
        <div className="bg-white border-b px-6 py-4">
          <div className="max-w-5xl mx-auto flex items-center gap-3">
            {ticket.logoUrl && <img src={ticket.logoUrl} alt="" className="h-8" />}
            <div className="flex-1">
              <h1 className="font-bold text-gray-900">{ticket.companyName}</h1>
              <ReviewSubtitle ticketNumber={ticket.ticketNumber} />
            </div>
            {pdfUrl && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="header-download-pdf"
                className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors"
              >
                <Download size={16} />
                Download PDF
              </a>
            )}
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-4 space-y-4">
          <div className={`border rounded-xl p-6 text-center ${bannerBg}`}>
            <BannerIcon className={`w-12 h-12 mx-auto mb-3 ${bannerIconColor}`} />
            <h1 className="text-xl font-bold text-gray-900 mb-2">{bannerTitle}</h1>
            <p className="text-gray-600">
              {isRejected
                ? `${approverStr ? `Revision requested by ${approverStr}` : 'Revision requested'}${formattedDate ? ` on ${formattedDate}` : ''}.`
                : isInvoiced
                  ? `This ticket has already been invoiced${formattedDate ? ` on ${formattedDate}` : ''}. No approval is required; you may download a copy for your records.`
                  : `${approverStr ? `Approved by ${approverStr}` : 'Approved'}${formattedDate ? ` on ${formattedDate}` : ''}.`
              }
            </p>
          </div>

          {ticket.stampedFile && <StampedFileNotice stampedFile={ticket.stampedFile} />}

          {pdfUrl && <TicketPdfViewer pdfUrl={pdfUrl} testId="ticket-pdf-viewer-readonly" />}
        </div>
      </div>
    );
  }

  if (errorType) {
    return (
      <div className="min-h-full bg-gray-50">
        <div className="bg-white border-b px-6 py-4">
          <div className="max-w-5xl mx-auto">
            <h1 className="font-bold text-gray-900">FieldIQ</h1>
            <p className="text-sm text-gray-500">Field Ticket Review</p>
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-8">
          <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
            {errorType === 'not_found' ? (
              <>
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-900 mb-2">Link Invalid or Expired</h1>
                <p className="text-gray-600">{errorMessage}</p>
              </>
            ) : errorType === 'conflict' ? (
              <>
                <AlertTriangle className="w-16 h-16 text-amber-400 mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-900 mb-2">Ticket Unavailable</h1>
                <p className="text-gray-600">{errorMessage}</p>
              </>
            ) : (
              <>
                <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
                <h1 className="text-xl font-bold text-gray-900 mb-2">Connection Issue</h1>
                <p className="text-gray-600 mb-4">{errorMessage}</p>
                <button
                  onClick={loadTicket}
                  className="inline-flex items-center gap-2 bg-amber-600 text-white px-6 py-2.5 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors"
                >
                  <RefreshCw size={16} />
                  Try Again
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) return null;

  return (
    <div className="min-h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b px-6 py-4">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          {ticket.logoUrl && <img src={ticket.logoUrl} alt="" className="h-8" />}
          <div className="flex-1">
            <h1 className="font-bold text-gray-900">{ticket.companyName}</h1>
            <ReviewSubtitle ticketNumber={ticket.ticketNumber} />
          </div>
          {pdfUrl && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="header-download-pdf"
              className="inline-flex items-center gap-2 bg-amber-600 text-white px-4 py-2 rounded-xl text-sm font-semibold hover:bg-amber-700 transition-colors"
            >
              <Download size={16} />
              Download PDF
            </a>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 py-4 space-y-4">
        {/* Capped at the viewport and scrolled inside, so the approval controls
            are not three screens below a multi-page ticket. */}
        {pdfUrl && <TicketPdfViewer pdfUrl={pdfUrl} fitViewport />}

        {/* HOW to approve — asked before anything is filled in, because the two
            answers are exclusive in the database and a page that collected both
            would be promising storage that does not exist. Rendered as a pair of
            radios rather than tabs: a tab strip says "two views of one thing",
            and these are two different things, only one of which will happen. */}
        {!showReject && (
          <fieldset
            className="bg-white rounded-xl shadow-sm border p-5"
            data-testid="approval-path-choice"
          >
            <legend className="font-bold text-gray-900 px-1">How would you like to approve?</legend>
            <div className="grid sm:grid-cols-2 gap-3 mt-2">
              {([
                {
                  value: 'form' as const,
                  title: liveStamp ? 'Fill in the approval stamp' : 'Approve on this page',
                  hint: liveStamp
                    ? `Complete ${ticket.stamp && !ticket.stamp.frozen ? ticket.stamp.operator_name : 'the'} stamp and sign here.`
                    : 'Confirm the ticket above and approve.',
                },
                {
                  value: 'upload' as const,
                  title: 'Upload a signed copy',
                  hint: 'Attach a photo or PDF of the ticket you already signed.',
                },
              ]).map(opt => (
                <label
                  key={opt.value}
                  className={`flex gap-3 items-start border-2 rounded-xl p-3 cursor-pointer transition-colors ${
                    path === opt.value ? 'border-amber-500 bg-amber-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="approval-path"
                    className="mt-1 accent-amber-600"
                    value={opt.value}
                    checked={path === opt.value}
                    disabled={submitting}
                    onChange={() => {
                      setPath(opt.value);
                      // Drop the other path's refusal. Leaving a 415 about a file
                      // on screen while the stamp form is showing attributes an
                      // error to the thing the customer is now looking at.
                      setUploadError(null);
                    }}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-gray-900">{opt.title}</span>
                    <span className="block text-xs text-gray-500 mt-0.5">{opt.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </fieldset>
        )}

        {/* Approver Info — the operator's stamp takes its place when the customer has one */}
        {path === 'upload' ? (
          <ApprovalFileUpload
            approverName={approverName}
            onApproverNameChange={setApproverName}
            file={uploadFile}
            onFileChange={f => { setUploadFile(f); setUploadError(null); }}
            error={uploadError}
            submitting={submitting}
            onSubmit={handleUpload}
            // The operator's own word for the signer when it has a stamp
            // ("Client Representative"), so the customer is asked for the same
            // thing under the same name on either path.
            nameLabel={liveStamp ? signerNameLabel(liveStamp) : 'Your name'}
            pdfUrl={pdfUrl}
          />
        ) : liveStamp ? (
          <ApprovalStampForm
            stamp={liveStamp}
            values={effectiveStampValues}
            onValueChange={(fieldId, value) => setStampValues(v => ({ ...v, [fieldId]: value }))}
            signerName={approverName}
            onSignerNameChange={setApproverName}
            onSignatureChange={setSignature}
            disabled={submitting}
          />
        ) : (
        <div className="bg-white rounded-xl shadow-sm border p-5">
          <h3 className="font-bold text-gray-900 mb-3">Your Information (optional)</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs text-gray-500 mb-1">Name</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="e.g. John Smith"
                value={approverName}
                onChange={e => setApproverName(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-xs text-gray-500 mb-1">Title</label>
              <input
                className="w-full border rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                placeholder="e.g. Company Man"
                value={approverTitle}
                onChange={e => setApproverTitle(e.target.value)}
              />
            </div>
          </div>
        </div>
        )}

        {/* Actions */}
        {showReject ? (
          <div className="bg-white rounded-xl shadow-sm border p-5 mb-8" data-testid="reject-panel">
            <h3 className="font-bold text-gray-900 mb-3">Request Revision</h3>
            {/* Focused on open: Request Revision is pressed from the sticky
                bar, often while the reader is still at the top of the PDF, and
                this panel is below it. Focus scrolls it into view. */}
            <textarea
              ref={rejectReasonRef}
              className="w-full border rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
              rows={3}
              placeholder="Please describe what needs to be corrected..."
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
            />
            <div className="flex gap-3 mt-3">
              <button
                onClick={() => setShowReject(false)}
                className="flex-1 border border-gray-300 text-gray-700 py-2.5 rounded-xl text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleReject}
                disabled={!rejectReason.trim() || submitting}
                className="flex-1 bg-red-600 text-white py-2.5 rounded-xl text-sm font-semibold disabled:opacity-40"
              >
                {submitting ? 'Sending...' : 'Send Back for Revision'}
              </button>
            </div>
          </div>
        ) : (
          <>
            {/* THE DECISION BAR. Sticky to the bottom of the screen so Approve
                and Request Revision are reachable while the reader is still in
                the PDF; once scrolled to, it rests here, under the approval
                fields it acts on. Sticky rather than fixed, so nothing below
                has to be padded to clear it.

                The upload path carries its own submit button, inside the panel
                that holds the name and the file — one card, one action. This
                page-level Approve belongs to the form path only, and rendering
                both would put two approve buttons on screen doing different
                things to different data. */}
            <div
              data-testid="review-action-bar"
              className="sticky bottom-0 z-10 -mx-6 px-6 py-3 bg-white border-t shadow-[0_-4px_12px_rgba(0,0,0,0.06)] space-y-2"
            >
              {path !== 'upload' && missingForApproval.length > 0 && (
                <p className="text-sm text-gray-600" data-testid="approve-missing">
                  Still needed to approve: {missingForApproval.join(', ')}
                </p>
              )}
              <div className="flex gap-3">
                {path !== 'upload' && (
                <button
                  onClick={handleApprove}
                  disabled={submitting || missingForApproval.length > 0}
                  className="flex-1 bg-green-600 text-white py-3 rounded-xl text-base font-semibold hover:bg-green-700 transition-colors disabled:opacity-40 flex items-center justify-center gap-2"
                >
                  {submitting ? <Loader2 size={18} className="animate-spin" /> : <CheckCircle size={18} />}
                  {submitting ? 'Approving...' : 'Approve Ticket'}
                </button>
                )}
                <button
                  onClick={() => setShowReject(true)}
                  disabled={submitting}
                  className="flex-1 border-2 border-gray-300 text-gray-700 py-3 rounded-xl text-base font-medium hover:border-red-300 hover:text-red-600 transition-colors flex items-center justify-center gap-2"
                >
                  <XCircle size={18} />
                  Request Revision
                </button>
              </div>
            </div>
            {/* The third copy of this link, and all three are wanted. The one
                in the header serves someone who only came for a copy; the one
                inside the upload panel is step one of that path; this one is
                for the reader who has scrolled to the decision. It sits under
                the bar rather than in it, so the pinned bar stays two buttons
                wide on a phone. Rendered only on the on-page path, because the
                upload panel's own copy is a few hundred pixels above it and two
                identical buttons in one column read as two different documents. */}
            {pdfUrl && path !== 'upload' && (
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="actions-download-pdf"
                className="w-full border-2 border-gray-200 text-gray-600 py-3 rounded-xl text-base font-medium hover:border-amber-300 hover:text-amber-700 transition-colors flex items-center justify-center gap-2"
              >
                <Download size={18} />
                Download PDF
              </a>
            )}
            <div className="pb-8" />
          </>
        )}
      </div>
    </div>
  );
};

export default TicketReviewPage;
